import { GranularityEnum, GranularityEnumConfig } from '../constants';

export type GranularityOption = {
  id: GranularityEnum;
  displayLabel: string;
  value: number;
};

/**
 * Get all granularity options as an array
 */
export const getGranularityOptions = (): GranularityOption[] => {
  return Object.values(GranularityEnum).map((id) => {
    const config = GranularityEnumConfig[id];
    return {
      id,
      displayLabel: config.displayLabel,
      value: config.value,
    };
  });
};

/**
 * Calculate time range duration in milliseconds
 */
export const calculateTimeRangeInMilliseconds = (
  startTime: number,
  endTime: number,
): number => endTime - startTime;

/**
 * Filter granularity options based on time range using milliseconds
 * Only returns options where granularity value <= time range duration
 * If time range is less than 1 minute, returns only 1m option
 */
export const getFilteredGranularityOptions = (
  startTime: number,
  endTime: number,
): GranularityOption[] => {
  const timeRangeInMs = calculateTimeRangeInMilliseconds(startTime, endTime);
  const allOptions = getGranularityOptions();

  // If time range is less than 1 minute, only return 1m option
  if (timeRangeInMs < 60 * 1000) {
    return allOptions.filter((option) => option.id === GranularityEnum['1m']);
  }

  // Filter options where granularity value <= time range duration
  return allOptions.filter((option) => option.value <= timeRangeInMs);
};

/**
 * Find the best granularity option when current selection is invalid
 * Returns the largest granularity option that fits within the time range
 * If time range is less than 1 minute, returns 1m
 */
export const getBestGranularityForTimeRange = (
  startTime: number,
  endTime: number,
): GranularityEnum => {
  const timeRangeInMs = calculateTimeRangeInMilliseconds(startTime, endTime);

  // If time range is less than 1 minute, always return 1m
  if (timeRangeInMs < 60 * 1000) {
    return GranularityEnum['1m'];
  }

  const filteredOptions = getFilteredGranularityOptions(startTime, endTime);

  if (filteredOptions.length === 0) {
    // Fallback to smallest granularity if no options fit
    return GranularityEnum['1m'];
  }

  // Return the largest granularity option (highest value in milliseconds)
  const bestOption = filteredOptions.reduce((prev, current) => {
    return current.value > prev.value ? current : prev;
  });

  return bestOption.id;
};

/**
 * Check if a granularity is valid for the given time range using milliseconds
 */
export const isGranularityValidForTimeRange = (
  granularityId: GranularityEnum,
  startTime: number,
  endTime: number,
): boolean => {
  const granularityConfig = GranularityEnumConfig[granularityId];
  if (!granularityConfig) return false;

  const timeRangeInMs = calculateTimeRangeInMilliseconds(startTime, endTime);
  return granularityConfig.value <= timeRangeInMs;
};

/**
 * Auto-adjust granularity when time range changes
 * If current granularity is invalid, select the best available option
 */
export const getAdjustedGranularity = (
  currentGranularity: GranularityEnum,
  startTime: number,
  endTime: number,
): GranularityEnum => {
  if (isGranularityValidForTimeRange(currentGranularity, startTime, endTime)) {
    return currentGranularity;
  }

  return getBestGranularityForTimeRange(startTime, endTime);
};
